import React from 'react';
import { RobbieMark } from '../../ui';
import { OutreachAction, SupplierThread } from './types';
import { isCounterCard } from '../../../hooks/outreachActionModel';
import ActionCard from './ActionCard';
import CounterOfferCard from './CounterOfferCard';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * Every move in full — title, reasoning, working, and its own two controls —
 * lifted over the lower part of the conversation instead of parked above the
 * composer. It is the bar's detail view, so it carries the moves that did not
 * fit on the bar's one line as well as the ones that did.
 *
 * The sheet is absolutely positioned by its caller and overlays; it never
 * pushes the messages, and nothing in here reaches a supplier — the asks land
 * in the buyer's box and the composer's send is still the only way out.
 *
 * A viewer gets the full reasoning and none of the two controls — the sheet is
 * where the thinking lives, and the thinking is worth reading either way.
 */
interface MovesSheetProps {
  thread: SupplierThread;
  actions: OutreachAction[];
  onStage: (actions: OutreachAction[], approvedPrice?: number) => void;
  onDismiss: (actionId: string) => void;
  stagedIds: string[];
  onConfirmPrice: (price: number | null) => void;
  busy?: boolean;
}

const MovesSheet: React.FC<MovesSheetProps> = ({
  thread,
  actions,
  onStage,
  onDismiss,
  stagedIds,
  onConfirmPrice,
  busy,
}) => {
  const readOnly = useReadOnly();

  return (
  <div className="rounded-card border border-robbie-border bg-ai-panel shadow-floating">
    <div className="flex items-center gap-s2 border-b border-robbie-border-soft px-[12px] py-s2">
      <RobbieMark size="xxs" aria-label="Robbie's suggested moves" />
      <span className="text-meta font-bold text-ink">What I&rsquo;d do next with {thread.supplierName}</span>
      <span className="text-[10.5px] text-ink-muted">
        {readOnly
          ? "Robbie's reasoning — acting on it is the RFQ owner's call."
          : 'Nothing here goes out — an ask lands in your box and waits for your send.'}
      </span>
    </div>
    <div className="flex max-h-[248px] flex-col gap-s2 overflow-y-auto px-[12px] py-s3">
      {actions.map((action) =>
        isCounterCard(action) ? (
          <CounterOfferCard
            key={action.id}
            thread={thread}
            action={action}
            onStage={(approvedPrice) => onStage([action], approvedPrice)}
            onDismiss={() => onDismiss(action.id)}
            onConfirmPrice={onConfirmPrice}
            staged={stagedIds.includes(action.id)}
            disabled={busy}
          />
        ) : (
          <ActionCard
            key={action.id}
            action={action}
            staged={stagedIds.includes(action.id)}
            disabled={busy}
            onStage={() => onStage([action])}
            onDismiss={() => onDismiss(action.id)}
          />
        ),
      )}
    </div>
  </div>
  );
};

export default MovesSheet;
